import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Keyboard,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from 'react-native';
import { AppButton } from '../../components/AppButton';
import { AppLoader } from '../../components/AppLoader';
import { EmptyState } from '../../components/EmptyState';
import {
  RequestFormDateField,
  RequestFormTextField,
} from '../../components/RequestFormField';
import { RequestCancelActions } from '../../components/RequestCancelActions';
import { PERMISSIONS } from '../../constants/permissions';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  cancelCompensatoryWorkRequest,
  createCompensatoryWorkRequest,
  decideCompensatoryWorkRequest,
  getCompensatoryWorkRequests,
} from '../../services/compensatoryWorkService';
import type { CompensatoryWorkRequest } from '../../types/compensatoryWork';
import type { LeaveBalance, LeaveDurationOption } from '../../types/leave';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import {
  compensatoryCreditLeaveName,
  compensatoryLeaveTypeLabel,
  compensatoryWorkPolicyText,
  configuredDurationDays,
} from '../../utils/compensatoryLeaveCredit';
import {
  COMP_OFF_PAST_WORK_DATE_MESSAGE,
  isCompensatoryWorkDateOpen,
} from '../../utils/compensatoryWorkWindow';
import {
  formatCalendarDate,
  parseCalendarDateInput,
  todayBusinessCalendarDate,
} from '../../utils/dateTime';
import { leaveErrorMessage } from '../../utils/leaveErrors';
import { isPendingWorkflowActionStatus } from '../../utils/workflowStatus';

export function CompensatoryWorkPanel({
  approvalOnly = false,
  leaveBalances = [],
  leaveDurations = [],
  focusRequestId = null,
}: {
  approvalOnly?: boolean;
  leaveBalances?: LeaveBalance[];
  leaveDurations?: LeaveDurationOption[];
  focusRequestId?: number | null;
}) {
  const auth = useAuth(),
    palette = useAppColors(),
    canApprove = auth.hasPermission(PERMISSIONS.LEAVE_APPROVE);
  const [items, setItems] = useState<CompensatoryWorkRequest[]>([]),
    [holidayDate, setHolidayDate] = useState(''),
    [reason, setReason] = useState(''),
    [reviewingId, setReviewingId] = useState<number | null>(null),
    [businessRequired, setBusinessRequired] = useState(false),
    [managerJustification, setManagerJustification] = useState(''),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const scrollRef = useRef<ScrollView>(null);
  const applyFieldRef = useRef<View>(null);
  const reviewFieldRef = useRef<View>(null);
  const focusedFieldRef = useRef<View>(null);
  const scrollOffset = useRef(0);
  const revealFocusedField = useCallback(() => {
    const delay = Platform.OS === 'ios' ? 80 : 160;
    setTimeout(() => {
      const target = focusedFieldRef.current;
      const scroll = scrollRef.current;
      if (!target || !scroll) return;
      target.measureInWindow((_x, y, _w, h) => {
        const keyboardTop =
          Keyboard.metrics()?.screenY ?? y + h + spacing.xl;
        const covered = y + h + spacing.md - keyboardTop;
        if (covered > 0) {
          scroll.scrollTo({
            y: scrollOffset.current + covered,
            animated: true,
          });
        }
      });
    }, delay);
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getCompensatoryWorkRequests());
      setError(null);
    } catch (e) {
      setError(
        leaveErrorMessage(e, 'Unable to load compensatory work requests.'),
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);
  useEffect(() => {
    if (!focusRequestId || !canApprove) return;
    const focused = items.find(
      item => item.compensatoryWorkRequestId === focusRequestId,
    );
    if (!focused) return;
    if (isPendingWorkflowActionStatus(focused.statusCode)) {
      setReviewingId(focused.compensatoryWorkRequestId);
    }
  }, [canApprove, focusRequestId, items]);
  useEffect(() => {
    const sub = Keyboard.addListener('keyboardDidShow', revealFocusedField);
    return () => sub.remove();
  }, [revealFocusedField]);
  useEffect(() => {
    if (Platform.OS !== 'android') return undefined;
    const show = Keyboard.addListener('keyboardDidShow', event => {
      setKeyboardInset(event.endCoordinates.height);
    });
    const hide = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardInset(0);
    });
    return () => {
      show.remove();
      hide.remove();
    };
  }, []);
  const todayDate = todayBusinessCalendarDate();
  const todayPickerMin = parseCalendarDateInput(todayDate) ?? undefined;
  const submit = async () => {
    if (!holidayDate || !reason.trim()) {
      setError('Work date and genuine business reason are required.');
      return;
    }
    if (!isCompensatoryWorkDateOpen(holidayDate, todayDate)) {
      setError(COMP_OFF_PAST_WORK_DATE_MESSAGE);
      return;
    }
    setBusy(true);
    try {
      await createCompensatoryWorkRequest({
        holidayDate,
        businessReason: reason.trim(),
      });
      setHolidayDate('');
      setReason('');
      await load();
    } catch (e) {
      setError(
        leaveErrorMessage(e, 'Unable to submit compensatory work request.'),
      );
    } finally {
      setBusy(false);
    }
  };
  const resetReview = () => {
    setReviewingId(null);
    setBusinessRequired(false);
    setManagerJustification('');
  };
  const submitDecision = async (item: CompensatoryWorkRequest) => {
    if (!managerJustification.trim()) {
      setError('Reason is required.');
      return;
    }
    if (
      businessRequired &&
      !isCompensatoryWorkDateOpen(item.holidayDate, todayDate)
    ) {
      setError(COMP_OFF_PAST_WORK_DATE_MESSAGE);
      return;
    }
    setBusy(true);
    try {
      await decideCompensatoryWorkRequest(
        item.compensatoryWorkRequestId,
        {
          businessRequirementConfirmed: businessRequired,
          comments: managerJustification.trim(),
        },
      );
      resetReview();
      await load();
    } catch (e) {
      setError(leaveErrorMessage(e, 'Unable to submit manager decision.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ScrollView
      ref={scrollRef}
      style={s.flex}
      refreshControl={
        <RefreshControl
          refreshing={loading}
          onRefresh={load}
          tintColor={palette.primary}
        />
      }
      contentContainerStyle={[s.body, { paddingBottom: spacing.xxl + keyboardInset }]}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="interactive"
      automaticallyAdjustKeyboardInsets
      onScroll={event => {
        scrollOffset.current = event.nativeEvent.contentOffset.y;
      }}
      scrollEventThrottle={16}
    >
      {!approvalOnly ? (
        <>
          <Text style={[s.policy, { color: palette.textSecondary }]}>
            {compensatoryWorkPolicyText({
              leaveTypeName: compensatoryCreditLeaveName(leaveBalances),
              fullDayLeaveDays: configuredDurationDays(leaveDurations, false),
              halfDayLeaveDays: configuredDurationDays(leaveDurations, true),
            })}
          </Text>
          <RequestFormDateField
            label="Approved work date"
            required
            value={holidayDate}
            onChange={v => {
              setHolidayDate(v);
              setError(null);
            }}
            minimumDate={todayPickerMin}
            editable={!busy}
          />
          <View ref={applyFieldRef} collapsable={false}>
          <RequestFormTextField
            label="Business requirement"
            required
            value={reason}
            onChangeText={v => {
              setReason(v);
              setError(null);
            }}
            placeholder="Explain the genuine company benefit"
            multiline
            editable={!busy}
            onFocus={() => {
              focusedFieldRef.current = applyFieldRef.current;
              revealFocusedField();
            }}
          />
          </View>
        </>
      ) : null}
      {error ? (
        <Text accessibilityRole="alert" style={{ color: palette.error }}>
          {error}
        </Text>
      ) : null}
      {!approvalOnly ? (
        <AppButton
          title="Submit Compensatory Work Request"
          onPress={submit}
          loading={busy}
        />
      ) : null}
      {!approvalOnly ? (
        <Text style={[s.heading, { color: palette.textPrimary }]}>
          {canApprove ? 'Compensatory requests' : 'My compensatory requests'}
        </Text>
      ) : null}
      {loading && items.length === 0 ? (
        <AppLoader message="Loading requests" />
      ) : items.length === 0 ? (
        <EmptyState
          title="No requests"
          message="No compensatory work requests were found."
        />
      ) : (
        items.map(item => (
          <View
            key={item.compensatoryWorkRequestId}
            style={[
              s.card,
              { borderColor: palette.border, backgroundColor: palette.surface },
              focusRequestId === item.compensatoryWorkRequestId
                ? { borderColor: palette.primary }
                : null,
            ]}
          >
            <Text style={[s.title, { color: palette.textPrimary }]}>
              {item.employeeName || 'My request'} ·{' '}
              {formatCalendarDate(item.holidayDate)}
            </Text>
            <Text style={{ color: palette.textSecondary }}>
              {item.businessReason}
            </Text>
            <Text
              style={[
                s.status,
                {
                  color:
                    item.statusCode === 'REJECTED'
                      ? palette.error
                      : palette.primary,
                },
              ]}
            >
              {formatDisplayLabel(item.statusCode)}
              {item.statusCode === 'APPROVED'
                ? item.earnedDays > 0
                  ? ` · earned ${item.earnedDays} ${compensatoryLeaveTypeLabel(item.earnedLeaveTypeName || item.earnedLeaveTypeCode) || 'leave'} · LOP adjusted ${item.adjustedDays}`
                  : ' · awaiting check-in and check-out on the approved date'
                : ''}
            </Text>
            {item.statusCode === 'CANCELLED' && item.cancellationReason ? (
              <Text style={{ color: palette.textSecondary }}>
                Cancellation reason: {item.cancellationReason}
              </Text>
            ) : null}
            {item.canCancel ? (
              <RequestCancelActions
                visible
                palette={palette}
                startTitle="Cancel Request"
                confirmTitle="Confirm Cancel"
                keepTitle="Keep Request"
                busy={busy}
                error={error}
                onConfirm={async reason => {
                  setBusy(true);
                  try {
                    await cancelCompensatoryWorkRequest(
                      item.compensatoryWorkRequestId,
                      reason,
                    );
                    await load();
                  } catch (e) {
                    setError(
                      leaveErrorMessage(
                        e,
                        'Unable to cancel compensatory work request.',
                      ),
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            ) : null}
            {canApprove &&
            item.statusCode === 'PENDING' &&
            item.employeeId !== auth.user?.employeeId ? (
              <>
                {!isCompensatoryWorkDateOpen(item.holidayDate, todayDate) ? (
                  <Text style={{ color: palette.error }}>
                    {COMP_OFF_PAST_WORK_DATE_MESSAGE}
                  </Text>
                ) : null}
                <AppButton
                  title="Review business requirement"
                  onPress={() => {
                    resetReview();
                    setReviewingId(item.compensatoryWorkRequestId);
                  }}
                  disabled={busy}
                />
              </>
            ) : null}
            {reviewingId === item.compensatoryWorkRequestId ? (
              <View style={[s.review, { borderColor: palette.border }]}>
                <Text style={[s.reviewHeading, { color: palette.textPrimary }]}>Strict business review</Text>
                <Text style={[s.reviewText, { color: palette.textPrimary }]}>Does this holiday work provide a genuine business requirement or benefit to the company?</Text>
                <View style={s.yesNoRow}>
                  <Text style={{ color: businessRequired ? palette.textSecondary : palette.error }}>No</Text>
                  <Switch
                    value={
                      businessRequired &&
                      isCompensatoryWorkDateOpen(item.holidayDate, todayDate)
                    }
                    onValueChange={value => {
                      if (
                        value &&
                        !isCompensatoryWorkDateOpen(item.holidayDate, todayDate)
                      ) {
                        setBusinessRequired(false);
                        setError(COMP_OFF_PAST_WORK_DATE_MESSAGE);
                        return;
                      }
                      setBusinessRequired(value);
                    }}
                    disabled={busy}
                    trackColor={{ true: palette.primary }}
                  />
                  <Text style={{ color: businessRequired ? palette.primary : palette.textSecondary }}>Yes</Text>
                </View>
                <View ref={reviewFieldRef} collapsable={false}>
                <RequestFormTextField
                  label="Reason"
                  required
                  value={managerJustification}
                  onChangeText={value => {
                    setManagerJustification(value);
                    setError(null);
                  }}
                  placeholder="Enter the reason for this decision"
                  multiline
                  editable={!busy}
                  onFocus={() => {
                    focusedFieldRef.current = reviewFieldRef.current;
                    revealFocusedField();
                  }}
                />
                </View>
                <AppButton
                  title={businessRequired ? 'Submit Approval' : 'Submit Rejection'}
                  onPress={() => submitDecision(item)}
                  disabled={busy || !managerJustification.trim()}
                />
                <AppButton title="Cancel" onPress={resetReview} disabled={busy} />
              </View>
            ) : null}
          </View>
        ))
      )}
    </ScrollView>
  );
}
const s = StyleSheet.create({
  flex: { flex: 1, minHeight: 0 },
  body: { padding: spacing.lg, gap: spacing.md },
  policy: { ...typography.body, lineHeight: 21 },
  heading: { ...typography.sectionTitle, marginTop: spacing.md },
  card: {
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.md,
    gap: spacing.sm,
  },
  title: { ...typography.body, fontWeight: '600' },
  status: { ...typography.caption, fontWeight: '700' },
  review: { borderTopWidth: 1, paddingTop: spacing.md, gap: spacing.sm },
  reviewHeading: { ...typography.body, fontWeight: '700' },
  reviewText: { ...typography.caption, flex: 1 },
  yesNoRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
});
